/**
 * The vanilla-DOM renderer (Deliverable B) behind the §6.4 `Renderer` seam.
 *
 * The `Renderer` interface (`mount` / `render` / `update`) is the swappable seam.
 * This v1 implementation uses no framework. It:
 *   - emits semantic, structure-first markup with a real heading outline
 *     (h2 → h3 → h4) and ARIA names (`aria-labelledby`) on every grouping, so a
 *     screen reader can navigate sections, claims and the three integrity lanes;
 *   - gives every node its `data-token` + `tok-*` class so the design tokens
 *     (CSS custom properties) paint it, with AA contrast (see `tokens`);
 *   - pulls EVERY human string through the injected `I18nProvider` — no literal
 *     copy lives in the renderer (INV-STABLE-ID at the boundary);
 *   - uses logical CSS only (`tokens.structuralCss`), so layout is
 *     direction-agnostic and RTL is a catalog change, not a render change;
 *   - renders typed `PrecisionLoss` as a visible, informational, freely
 *     dismissable note (Deliverable D);
 *   - is deterministic: heading ids are a per-render DFS counter (no time/random),
 *     so the serialized DOM is snapshot-stable for Agent 2's visual lane (C).
 *
 * `dirHint` is `'auto'` (direction is L5's job); the renderer sets `dir="auto"` on
 * the root and otherwise stays direction-agnostic. Hosts/controllers set a concrete
 * `dir` from the provider on their wrapper.
 */
import { buildStylesheet, DEFAULT_THEME } from './tokens.js';
const STYLE_ID = 'pv-stylesheet';
/** Resolve a mount target that may be an element or a selector/id string. */
function resolveHost(host) {
    if (typeof host !== 'string')
        return host;
    const doc = globalThis.document;
    const byId = doc.getElementById(host);
    if (byId)
        return byId;
    const bySelector = doc.querySelector(host);
    if (bySelector instanceof HTMLElement)
        return bySelector;
    throw new Error(`DomRenderer.mount: no element found for host "${host}"`);
}
export class DomRenderer {
    host = null;
    locale;
    theme;
    styleId;
    /** Per-render counter for deterministic, unique heading ids (snapshot-stable). */
    idSeq = 0;
    constructor(options = {}) {
        this.locale = options.locale ?? 'en';
        this.theme = options.theme ?? DEFAULT_THEME;
        this.styleId = options.styleId ?? STYLE_ID;
    }
    mount(host) {
        this.host = resolveHost(host);
    }
    render(vm, t) {
        const host = this.requireHost();
        const doc = host.ownerDocument;
        host.replaceChildren(); // full re-render (see `update`)
        this.ensureStylesheet(doc);
        this.idSeq = 0; // reset so ids are deterministic for this view-model
        // The root node IS the region: one <section role="region"> named by its heading.
        const root = this.renderNode(vm.root, t, doc, 0);
        root.classList.add('pv-root');
        root.setAttribute('role', 'region');
        root.dir = vm.dirHint; // 'auto' — direction stays L5's concern
        root.dataset['register'] = vm.register;
        // Aggregated precision-loss summary (registry-indexable list; informational).
        if (vm.precisionLoss.length > 0) {
            root.appendChild(this.renderLossSummary(vm.precisionLoss, t, doc));
        }
        host.appendChild(root);
    }
    /** v1 update is a full, deterministic re-render. An alternative diffing renderer
     * satisfies the same `Renderer` contract. */
    update(_prev, next, t) {
        this.render(next, t);
    }
    // --- internals ---------------------------------------------------------------
    requireHost() {
        if (this.host === null)
            throw new Error('DomRenderer: render() called before mount()');
        return this.host;
    }
    ensureStylesheet(doc) {
        if (doc.getElementById(this.styleId))
            return;
        const style = doc.createElement('style');
        style.id = this.styleId;
        style.textContent = buildStylesheet(this.theme);
        (doc.head ?? doc.documentElement).appendChild(style);
    }
    renderNode(node, t, doc, depth) {
        const hasChildren = node.children !== undefined && node.children.length > 0;
        const nonNeutral = node.token !== 'neutral';
        // Element semantics:
        //   leaf            → <div>
        //   non-neutral box → <article>            (claims, machine lane) — keeps a name
        //   neutral box     → <section> (depth ≤1) or <div role="group"> (deeper)
        let el;
        if (!hasChildren) {
            el = doc.createElement('div');
        }
        else if (nonNeutral) {
            el = doc.createElement('article');
        }
        else if (depth <= 1) {
            el = doc.createElement('section');
        }
        else {
            el = doc.createElement('div');
            el.setAttribute('role', 'group');
        }
        el.classList.add('pv-node');
        if (nonNeutral)
            el.classList.add(`tok-${node.token}`);
        el.dataset['token'] = node.token;
        const text = t.t(node.text.key, node.text.params, this.locale);
        if (hasChildren) {
            // a heading element (named outline) that also names the grouping via id.
            const headingTag = depth === 0 ? 'h2' : depth === 1 ? 'h3' : depth === 2 ? 'h4' : 'span';
            const heading = doc.createElement(headingTag);
            heading.className = 'pv-text';
            heading.id = `pv-h${this.idSeq++}`;
            heading.textContent = text;
            el.appendChild(heading);
            el.setAttribute('aria-labelledby', heading.id);
            for (const child of node.children) {
                el.appendChild(this.renderNode(child, t, doc, depth + 1));
            }
        }
        else {
            el.classList.add('pv-text');
            el.textContent = text;
        }
        if (node.loss) {
            for (const loss of node.loss)
                el.appendChild(this.renderLoss(loss, t, doc));
        }
        return el;
    }
    /**
     * One precision-loss note: `role="note"` (informational, never an error), with a
     * dismiss button (Deliverable D — freely dismissable). The dismiss button's
     * accessible name is the resolved note text (no hardcoded copy, INV-STABLE-ID).
     * Dismissal is ephemeral UI state (removes the element); it never touches the
     * view-model or any verdict.
     */
    renderLoss(loss, t, doc) {
        const el = doc.createElement('div');
        el.className = 'pv-loss';
        el.setAttribute('role', 'note');
        el.dataset['kind'] = loss.kind;
        el.dataset['severity'] = loss.severity;
        el.dataset['seam'] = loss.seamId;
        const text = doc.createElement('span');
        text.className = 'pv-loss-text';
        text.textContent = t.t(loss.dropped.key, loss.dropped.params, this.locale);
        el.appendChild(text);
        const dismiss = doc.createElement('button');
        dismiss.type = 'button';
        dismiss.className = 'pv-loss-dismiss';
        dismiss.textContent = '×'; // × — decorative glyph
        dismiss.setAttribute('aria-label', text.textContent); // localized name, no leak
        dismiss.dataset['action'] = 'dismiss-loss';
        dismiss.addEventListener('click', () => el.remove());
        el.appendChild(dismiss);
        return el;
    }
    renderLossSummary(losses, t, doc) {
        // a list of notes; each <li> names itself via its content (no hardcoded label).
        const list = doc.createElement('ul');
        list.className = 'pv-loss-summary';
        for (const loss of losses) {
            const li = doc.createElement('li');
            li.appendChild(this.renderLoss(loss, t, doc));
            list.appendChild(li);
        }
        return list;
    }
}
/** Convenience factory mirroring the interface-first style. */
export function createDomRenderer(options) {
    return new DomRenderer(options);
}
//# sourceMappingURL=renderer.js.map